/* eslint-disable @typescript-eslint/no-explicit-any */
import Axios, { AxiosRequestConfig } from 'axios';
import { auth } from 'src/auth/firebase';
import {
  COOKIE_KEYS,
  FORBIDDEN_STATUS_CODE,
  PUBLIC_API_ANALYTICS_PAGE_VIEW_ENDPOINT,
  PUBLIC_API_ENDPOINTS,
  UNAUTHORIZED_STATUS_CODE,
} from 'src/lib/constants';
import { getVitalyRequestHeaders, handleVitalyError } from 'src/services/integrations/VitalyService';
import { getBaseURL } from 'src/lib/utils/api.util';
import { getCookieValue } from './cookies';
import { setJwtToken } from 'src/lib/utils/auth.util';
import { signOut } from 'firebase/auth';
import { isVitalyImplementation } from 'src/lib/utils/vitaly.util';
import { getUserTimezone } from 'src/lib/utils/time.util';
import { captureLabeledSentryError, SENTRY_LABEL } from 'src/lib/utils/error.util';
import { AnalyticsPageCategory } from 'src/lib/api/generated/schemas';

const AXIOS_INTANCE = Axios.create({
  baseURL: getBaseURL()?.replace('/api', ''),
  headers: {
    'Content-Type': 'application/json',
  },
});

const checkAndRenewToken = async (currentToken?: string): Promise<string | null> => {
  if (!currentToken || !auth?.currentUser) return null;

  try {
    const payload = JSON.parse(atob(currentToken.split('.')[1]));
    const expirationTime = payload.exp * 1000;
    const fiveMinutes = 5 * 60 * 1000;

    if (Date.now() >= expirationTime - fiveMinutes) {
      const newToken = await auth.currentUser?.getIdToken(true);
      if (!newToken) return null;

      setJwtToken(newToken);

      return newToken;
    }
    return currentToken;
  } catch (error: any) {
    console.error('Token renewal error:', error);
    captureLabeledSentryError(SENTRY_LABEL.AUTH, 'Token renewal failed', { error });
    return null;
  }
};

const checkIfPublicEndpoint = (url?: string, data?: any) => {
  if (!url) return false;
  if (url === PUBLIC_API_ANALYTICS_PAGE_VIEW_ENDPOINT) {
    return data?.category === AnalyticsPageCategory.LOGIN;
  }

  return PUBLIC_API_ENDPOINTS.includes(url);
};

export const customAxiosInstance = async <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
  let headers: any;
  const isVitalyUser = isVitalyImplementation();

  const isServer = typeof window === 'undefined';
  if (!options?.headers?.authorization) {
    const user = auth.currentUser;
    if (!isServer && isVitalyUser) {
      headers = await getVitalyRequestHeaders(user);
    } else {
      const token = getCookieValue(COOKIE_KEYS.Jwt);
      const renewedToken = await checkAndRenewToken(token);
      headers = renewedToken ? { authorization: `Bearer ${renewedToken}` } : {};
    }
  } else {
    headers = options?.headers;
  }

  // Añadir la zona horaria a las cabeceras
  const timezone = getUserTimezone();
  headers = {
    ...headers,
    'X-Timezone': timezone,
  };

  const isPublicEndpoint = checkIfPublicEndpoint(config.url, config.data);

  const source = Axios.CancelToken.source();

  // Cancel the request if there is no jwt token yet (because on firt login the jwt is not set yet and the request should be canceled to avoid errors)
  if (
    (!headers?.authorization && !isPublicEndpoint) ||
    (isVitalyUser && !headers['Fisify-Authorization'] && !isPublicEndpoint)
  ) {
    source.cancel();
  }

  const makeRequest = async (headers: any) => {
    return AXIOS_INTANCE({
      ...config,
      ...options,
      cancelToken: source.token,
      headers,
    }).then((response: any) => response.data);
  };

  try {
    return await makeRequest(headers);
  } catch (error: any) {
    if (error.response?.status === UNAUTHORIZED_STATUS_CODE) {
      if (isVitalyUser) {
        handleVitalyError(error);
      } else {
        console.log('Auth error:', error);
        captureLabeledSentryError(SENTRY_LABEL.AUTH, 'Auth error Unauthorized', { error });
        if (auth.currentUser) {
          await signOut(auth);
        }
        throw error.response?.data;
      }
    }
    if (error.response?.status === FORBIDDEN_STATUS_CODE) {
      try {
        const renewedToken = await checkAndRenewToken(getCookieValue(COOKIE_KEYS.Jwt));
        if (renewedToken) {
          return await makeRequest({ ...headers, authorization: `Bearer ${renewedToken}` });
        }
      } catch (renewError) {
        console.error('Token renewal failed:', renewError);
        captureLabeledSentryError(SENTRY_LABEL.AUTH, 'Critical token renewal failure', {
          renewError,
        });
        if (auth.currentUser) {
          await signOut(auth);
        }
      }
    }

    throw error.response?.data || error;
  }
};
